import DatePicker from "react-datepicker";
import CalendarIcon from "@/shared/assets/images/CalendarBlank.svg"
import * as yup from 'yup'
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { getBonusTransactionsReportXlsx } from "@/shared/api/bonuses/bonuses";
import { FormInput } from "@/shared/ui/form-input";
import { selectAvailableBonuses } from "@/entities/bonus";
import { useAppSelector } from "@/shared/lib/redux";

interface BonusTransactionsReportFormValues {
    start_date?: Date,
    end_date?: Date,
    bonus_id?: string[],
}

const initial: BonusTransactionsReportFormValues = {
    start_date: undefined,
    end_date: undefined,
    bonus_id: [],
}

const validationSchema = yup.object().shape({
    start_date: yup.date().required("Укажите дату начала"),
    end_date: yup.date().min(yup.ref('start_date'), "Некорректная дата").required("Укажите дату окончания"),
    bonus_id: yup.array().of(yup.string()).optional(),
})

export const BonusTransactionsReportForm = () => {
    const bonuses = useAppSelector(selectAvailableBonuses)
    
    const bonusSelectOptions = bonuses.map(bonus => ({
        value: bonus.id,
        label: bonus.name
    }))

    const {
        control,
        watch,
        handleSubmit,
        setValue,
        formState: {
            isSubmitting,
        },
    } = useForm<BonusTransactionsReportFormValues>({
        defaultValues: initial,
        resolver: yupResolver(validationSchema)
    })

    const onSubmit = async (data: BonusTransactionsReportFormValues) => {
        try {
            const responseBlob = await getBonusTransactionsReportXlsx({
                endDate: data.end_date?.toISOString(),
                startDate: data.start_date?.toISOString(),
                bonusIds: data.bonus_id || undefined,
            })
            const url = window.URL.createObjectURL(new Blob([responseBlob.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('target', '_blank')
            link.setAttribute('download', 'bonus-transactions-report.xlsx')
            document.body.appendChild(link)
            link.click()
            link.parentNode?.removeChild(link)
        } catch (error) {
            console.error('Error creating report:', error)
        }
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full">
            <div className="flex flex-[1_1_auto] flex-col gap-2">
                <div className="flex flex-col gap-4">
                    <div>
                        <div className="mb-1 font-medium">
                            Начальная дата
                        </div>
                        <Controller
                            control={control}
                            name={'start_date'}
                            render={({ field }) => (
                                <DatePicker 
                                    selected={field.value}
                                    onChange={(date: Date | null) => setValue('start_date', date)}
                                    wrapperClassName="react-datepicker-form2"
                                    dateFormat={"dd.MM.yyyy"}
                                    showIcon
                                    placeholderText="Выберите начальную дату"
                                    enableTabLoop={false}
                                    popperProps={{
                                        placement: 'bottom'
                                    }}
                                    icon={(<CalendarIcon width={24} height={24}/>)}
                                />
                            )}
                        />
                    </div>
                    <div>
                        <div className="mb-1 font-medium">
                            Конечная дата
                        </div>
                        <Controller
                            control={control}
                            name={'end_date'}
                            render={({ field }) => (
                                <DatePicker 
                                    selected={field.value}
                                    minDate={watch('start_date')}
                                    onChange={(date: Date | null) => setValue('end_date', date)}
                                    wrapperClassName="react-datepicker-form2"
                                    dateFormat={"dd.MM.yyyy"}
                                    showIcon
                                    placeholderText="Выберите конечную дату"
                                    enableTabLoop={false}
                                    popperProps={{
                                        placement: 'bottom'
                                    }}
                                    icon={(<CalendarIcon width={24} height={24}/>)}
                                />
                            )}
                        />
                    </div>
                    <div>
                        <FormInput
                            label="Бонусы (необязательно)"
                            id="bonus_id"
                            options={bonusSelectOptions}
                            control={control}
                            type="select"
                            isMulti={true}
                            placeholder="Выберите бонусы"
                            required={false}
                        />
                    </div>
                </div>
            </div>
            <div className="flex flex-[0_0_auto] flex-col gap-2">
                <button
                    type="submit" 
                    className="w-[400px] py-4 text-lg rounded-xl font-bold text-white bg-color-primary hover:bg-color-primary/70 transition"
                    disabled={isSubmitting}
                >
                    Создать отчет
                </button>
            </div>
        </form>
    )
}

